/**
 * Cookie Consent Management
 *
 * Google Consent Mode v2 integration.
 * Stores consent preferences in cookies matching the budowle.pl pattern.
 *
 * Cookies:
 *   castlopedia_consent        — JSON with Google consent mode keys + accepted_at timestamp
 *   castlopedia_consent_accepted — flag ("1") indicating consent has been given
 */

export interface ConsentState {
  necessary: boolean;
  functionality: boolean;
  analytics: boolean;
  advertising: boolean;
  personalization: boolean;
}

export const CONSENT_COOKIE_NAME = 'castlopedia_consent';
export const CONSENT_ACCEPTED_COOKIE_NAME = 'castlopedia_consent_accepted';

/** Default state: only necessary cookies granted */
export const DEFAULT_CONSENT: ConsentState = {
  necessary: true,
  functionality: false,
  analytics: false,
  advertising: false,
  personalization: false,
};

export interface CategoryInfo {
  key: keyof ConsentState;
  title: string;
  description: string;
  alwaysActive?: boolean;
}

export const CATEGORIES: CategoryInfo[] = [
  {
    key: 'necessary',
    title: 'Necessary (always active)',
    description:
      'Necessary cookies are required to enable basic site functionality, such as secure login or managing consent settings. These cookies do not store any personally identifiable data.',
    alwaysActive: true,
  },
  {
    key: 'functionality',
    title: 'Functional',
    description:
      'Functional cookies help perform certain functionalities like sharing content on social media, collecting feedback, or supporting third-party features.',
  },
  {
    key: 'analytics',
    title: 'Analytical',
    description:
      'Analytical cookies help us understand how visitors interact with the website. They provide information about visitor count, bounce rate, traffic sources, and more.',
  },
  {
    key: 'advertising',
    title: 'Advertising',
    description:
      'Advertising cookies are used to deliver personalized advertisements based on the pages you visited and to analyze the effectiveness of advertising campaigns.',
  },
  {
    key: 'personalization',
    title: 'Personalization',
    description:
      'Personalization cookies allow us to remember your preferences and display personalized content tailored to your choices.',
  },
];

/* ── Cookie value types ─────────────────────────────────── */

interface ConsentCookieValue {
  ad_storage: 'granted' | 'denied';
  ad_user_data: 'granted' | 'denied';
  ad_personalization: 'granted' | 'denied';
  analytics_storage: 'granted' | 'denied';
  functionality_storage: 'granted' | 'denied';
  personalization_storage: 'granted' | 'denied';
  security_storage: 'granted' | 'denied';
  accepted_at: string;
}

/* ── Internal ↔ cookie format conversion ────────────────── */

function stateToCookieValue(state: ConsentState): ConsentCookieValue {
  return {
    ad_storage: state.advertising ? 'granted' : 'denied',
    ad_user_data: state.advertising ? 'granted' : 'denied',
    ad_personalization: state.advertising ? 'granted' : 'denied',
    analytics_storage: state.analytics ? 'granted' : 'denied',
    functionality_storage: state.functionality ? 'granted' : 'denied',
    personalization_storage: state.personalization ? 'granted' : 'denied',
    security_storage: 'granted',
    accepted_at: new Date().toISOString(),
  };
}

function cookieValueToState(value: ConsentCookieValue): ConsentState {
  return {
    necessary: true,
    functionality: value.functionality_storage === 'granted',
    analytics: value.analytics_storage === 'granted',
    advertising: value.ad_storage === 'granted',
    personalization: value.personalization_storage === 'granted',
  };
}

/* ── Public API ─────────────────────────────────────────── */

/**
 * Read consent state from cookie.
 * Returns null if no consent has been given yet.
 */
export function readConsentFromCookie(): ConsentState | null {
  if (typeof document === 'undefined') return null;

  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE_NAME}=([^;]*)`));
  if (!match) return null;

  try {
    const parsed: ConsentCookieValue = JSON.parse(decodeURIComponent(match[1]));
    return cookieValueToState(parsed);
  } catch {
    return null;
  }
}

/**
 * Save consent state to cookies (365 days expiry).
 * Writes both the main consent cookie and the accepted flag.
 */
export function saveConsentToCookie(state: ConsentState): void {
  if (typeof document === 'undefined') return;

  const value = encodeURIComponent(JSON.stringify(stateToCookieValue(state)));
  const maxAge = 31536000; // 365 days

  document.cookie = `${CONSENT_COOKIE_NAME}=${value};path=/;max-age=${maxAge};SameSite=Lax`;
  document.cookie = `${CONSENT_ACCEPTED_COOKIE_NAME}=1;path=/;max-age=${maxAge};SameSite=Lax`;
}

/**
 * Update Google Consent Mode via dataLayer.
 */
export function updateGoogleConsent(state: ConsentState): void {
  if (typeof window === 'undefined') return;

  const win = window as any;
  win.dataLayer = win.dataLayer || [];

  function gtag(...args: any[]) {
    win.dataLayer.push(args);
  }

  gtag('consent', 'update', {
    ad_storage: state.advertising ? 'granted' : 'denied',
    ad_user_data: state.advertising ? 'granted' : 'denied',
    ad_personalization: state.advertising ? 'granted' : 'denied',
    analytics_storage: state.analytics ? 'granted' : 'denied',
    functionality_storage: state.functionality ? 'granted' : 'denied',
    personalization_storage: state.personalization ? 'granted' : 'denied',
    security_storage: 'granted',
  });
}

/**
 * Check if user has made a consent choice (via the accepted flag cookie).
 */
export function hasConsentChoice(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie
    .split(';')
    .some((c) => c.trim().startsWith(CONSENT_ACCEPTED_COOKIE_NAME + '='));
}
